import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import FormErrors from "@/components/FormErrors/FormErrors.vue";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import { v4 as uuidv4 } from "uuid";
import { JSConn } from "@/utilities/js-conn";

import { TOAST_OPTIONS } from "@/constants";
import { NamedRts } from "@/router";
import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";

import {
  buildFormCommodity,
  prettifyCommodityStatus,
} from "@/models/Commodity";
import { Commodity } from "@/store/modules/commodities";

export default defineComponent({
  name: "commodityEditForm",
  components: {
    FormErrors,
    LoadingAnimation,
  },
  props: {
    commodityId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    let dataLoaded = ref(false);
    let loadingMessage = ref("Preparing the form...");

    let firstInput: any = ref(null);
    let deleteModal: any = ref(null);

    const showDeleteModal = () => {
      deleteModal.value.show();
    };

    const hideDeleteModal = () => {
      deleteModal.value.hide();
    };

    const deleting = ref(false);

    const deleteCommodity = () => {
      deleting.value = true;
      const jsConn = new JSConn();
      jsConn.toRoutePath = "/commodities";
      jsConn.formParams = {
        id: props.commodityId,
      };
      $store.dispatch("deleteCommodity", jsConn).then(jsConn => {
        deleting.value = false;
        ctx.root.$router.push(jsConn.toRoutePath);
        return Promise.resolve(jsConn);
      });
    };

    const cancelParams = () => {
      if (props.commodityId === "new") {
        return {
          name: NamedRts.commodities,
        };
      } else {
        return {
          name: NamedRts.commodities,
        };
      }
    };

    let {
      formCommodity,
      loadCommodityIntoForm,
      commodityStatuses,
      loadCommodityStatusesIntoForm,
    } = buildFormCommodity();

    const apiCommodity = computed(() => {
      return $store.getters.commodity as Commodity;
    });

    onMounted(() => {
      let dataPromises = [];
      let jsConn = new JSConn();
      loadCommodityStatusesIntoForm(commodityStatuses);

      if (props.commodityId !== "new") {
        jsConn.formParams = {
          id: props.commodityId,
        };

        dataPromises.push($store.dispatch("needCommodity", jsConn));
      } else {
        formCommodity.color = "hsla(276, 66%, 65%, 1)";
      }

      Promise.all(dataPromises)
        .then(() => {
          if (props.commodityId !== "new") {
            loadCommodityIntoForm(apiCommodity.value, formCommodity);
          }
        })
        .finally(() => {
          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        });
    });

    let { submitButtonText, submitting } = buildSubmitButton();
    let { hasFormErrors, formErrors } = buildFormErrors();

    const onSubmit = (evt: any) => {
      evt.preventDefault();
      const jsConn = new JSConn();

      hasFormErrors.value = false;
      formErrors.value = [];
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      let storeDispatch;

      if (props.commodityId === "new") {
        formCommodity.id = uuidv4();
        jsConn.formParams = formCommodity;
        storeDispatch = "createCommodity";
      } else {
        jsConn.formParams = formCommodity;
        storeDispatch = "updateCommodity";
      }

      $store
        .dispatch(storeDispatch, jsConn)
        .then(jsConn => {
          ctx.root.$bvToast.toast(
            "Commodity was successfully saved",
            TOAST_OPTIONS,
          );
          ctx.root.$router.push(cancelParams());
          return Promise.resolve(jsConn);
        })
        .catch(jsConn => {
          hasFormErrors.value = true;
          formErrors.value = jsConn.error["response"]["data"];
        })
        .finally(() => {
          submitting.value = false;
          submitButtonText.value = "Submit";
        });
    };

    return {
      dataLoaded,
      loadingMessage,
      firstInput,
      cancelParams,

      formCommodity,
      commodityStatuses,
      prettifyCommodityStatus,

      onSubmit,
      submitButtonText,
      submitting,
      hasFormErrors,
      formErrors,

      deleteModal,
      showDeleteModal,
      hideDeleteModal,
      deleting,
      deleteCommodity,
    };
  },
});
